import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import HttpsIcon from '@mui/icons-material/Https';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchWebserverInfo, fetchWebserverServers, type NginxServer } from '../api/webserver';
import {
  fetchSubdomains,
  addSubdomain,
  removeSubdomain,
  requestCert,
  type SubdomainConfig,
  type SubdomainEntry,
  type TargetType,
} from '../api/subdomains';
import { usePermission } from '../hooks/usePermission';

// ── Nginx info card ────────────────────────────────────────────────────────────

function InfoCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['webserver', 'info'],
    queryFn: fetchWebserverInfo,
    refetchInterval: 30_000,
  });

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
              {isLoading ? (
                <CircularProgress size={16} />
              ) : data?.active ? (
                <><CheckCircleIcon color="success" fontSize="small" /><Typography variant="body2">Running</Typography></>
              ) : (
                <><ErrorIcon color="error" fontSize="small" /><Typography variant="body2">Stopped</Typography></>
              )}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Version</Typography>
            <Typography variant="body2" mt={0.5}>{isLoading ? '…' : (data?.version ?? 'unknown')}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Server blocks</Typography>
            <Typography variant="body2" mt={0.5}>{isLoading ? '…' : (data?.server_block_count ?? 0)}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Server blocks tab ──────────────────────────────────────────────────────────

function ServerRow({ server }: { server: NginxServer }) {
  if (server.parse_error) {
    return (
      <TableRow>
        <TableCell colSpan={5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningAmberIcon color="warning" fontSize="small" />
            <Typography variant="body2" color="text.secondary">{server.source_file}</Typography>
            <Typography variant="caption" color="error.main">{server.error}</Typography>
          </Stack>
        </TableCell>
      </TableRow>
    );
  }
  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
          {server.tls && (
            <Tooltip title="TLS/HTTPS">
              <LockIcon fontSize="small" color="success" />
            </Tooltip>
          )}
          {server.server_names.map((n) => (
            <Chip key={n} label={n} size="small" variant="outlined" />
          ))}
        </Stack>
      </TableCell>
      <TableCell>
        <Stack spacing={0.25}>
          {server.listen.map((l, i) => (
            <Typography key={i} variant="caption" fontFamily="monospace">{l}</Typography>
          ))}
        </Stack>
      </TableCell>
      <TableCell>
        {server.proxy_pass
          ? <Chip label="proxy" size="small" color="info" variant="outlined" />
          : <Chip label="static" size="small" variant="outlined" />}
      </TableCell>
      <TableCell>
        <Typography variant="caption" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
          {server.proxy_pass ?? server.root ?? '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">{server.source_file}</Typography>
      </TableCell>
    </TableRow>
  );
}

function ServerBlocksTab({ canRead }: { canRead: boolean }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['webserver', 'servers'],
    queryFn: fetchWebserverServers,
    enabled: canRead,
    refetchInterval: 60_000,
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">Failed to load server blocks.</Alert>;

  return (
    <TableContainer component={Card}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Domain</TableCell>
            <TableCell>Listen</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Target</TableCell>
            <TableCell>Config file</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!data || data.servers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="body2" color="text.secondary" py={2}>
                  No server blocks found in /etc/nginx/sites-enabled/
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.servers.map((s, i) => <ServerRow key={i} server={s} />)
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Subdomains tab ─────────────────────────────────────────────────────────────

function SslChip({ ssl }: { ssl: boolean }) {
  return ssl
    ? <Chip icon={<HttpsIcon />} label="HTTPS" color="success" size="small" />
    : <Chip icon={<LockOpenIcon />} label="HTTP only" color="warning" size="small" variant="outlined" />;
}

interface AddDialogProps {
  open: boolean;
  rootDomain: string;
  onClose: () => void;
  onAdded: () => void;
}

function AddSubdomainDialog({ open, rootDomain, onClose, onAdded }: AddDialogProps) {
  const [domain, setDomain]         = useState('');
  const [targetType, setTargetType] = useState<TargetType>('static');
  const [target, setTarget]         = useState('');
  const [loading, setLoading]       = useState(false);
  const [err, setErr]               = useState('');

  const handleClose = () => {
    setDomain(''); setTargetType('static'); setTarget(''); setErr('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!domain) { setErr('Domain is required'); return; }
    setErr('');
    setLoading(true);
    try {
      await addSubdomain({ domain, target_type: targetType, target: target || undefined });
      onAdded();
      handleClose();
    } catch { /* global snackbar */ } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Subdomain</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField
            label="Domain"
            size="small"
            fullWidth
            value={domain}
            onChange={(e) => setDomain(e.target.value.toLowerCase())}
            placeholder={`sub.${rootDomain || 'example.com'}`}
            helperText="Full domain name, e.g. status.dustinity.de"
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={targetType} label="Type" onChange={(e) => setTargetType(e.target.value as TargetType)}>
              <MenuItem value="static">Static folder</MenuItem>
              <MenuItem value="proxy">Reverse proxy (URL)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={targetType === 'static' ? 'Folder path (optional)' : 'Proxy URL'}
            size="small"
            fullWidth
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder={
              targetType === 'static'
                ? `/opt/webadmin/subdomains/${domain || 'example.com'}`
                : 'http://127.0.0.1:8080'
            }
            helperText={
              targetType === 'static'
                ? 'Leave empty to use the default path'
                : 'Backend URL to proxy requests to'
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={() => void handleSubmit()} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface SubdomainsTabProps {
  canRead: boolean;
  canWrite: boolean;
  addOpen: boolean;
  onCloseAdd: () => void;
}

function SubdomainsTab({ canRead, canWrite, addOpen, onCloseAdd }: SubdomainsTabProps) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<SubdomainConfig>({
    queryKey: ['subdomains'],
    queryFn: fetchSubdomains,
    enabled: canRead,
  });

  const [certLoading, setCertLoading]     = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const refresh = () => void qc.invalidateQueries({ queryKey: ['subdomains'] });

  const handleCert = async (entry: SubdomainEntry) => {
    setCertLoading(entry.domain);
    try { await requestCert(entry.domain); refresh(); } finally { setCertLoading(null); }
  };

  const handleDelete = async (entry: SubdomainEntry) => {
    setDeleteLoading(entry.domain);
    try { await removeSubdomain(entry.domain); refresh(); } finally { setDeleteLoading(null); }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">Failed to load subdomain configuration.</Alert>;

  const cfg = data!;
  const primaryRow: SubdomainEntry = {
    domain: cfg.primary_domain || '—',
    target_type: 'static',
    target: '/opt/webadmin/frontend-dist',
    ssl: true,
    created_at: '',
  };
  const rows = [primaryRow, ...cfg.subdomains];

  return (
    <>
      {!cfg.certbot_email && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No certbot email configured — SSL requests will fail.
          Set <code>certbot_email</code> in <code>/etc/webadmin/subdomains.yaml</code>.
        </Alert>
      )}

      {cfg.root_domain && (
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          Root domain: <strong>{cfg.root_domain}</strong>
        </Typography>
      )}

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Domain</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>SSL</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => {
              const isPrimary = i === 0;
              return (
                <TableRow key={row.domain} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={isPrimary ? 700 : 400}>
                        {row.domain}
                      </Typography>
                      {isPrimary && <Chip label="primary" size="small" color="primary" variant="outlined" />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={row.target_type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {row.target}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <SslChip ssl={row.ssl} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {!row.ssl && canWrite && !isPrimary && (
                        <Tooltip title="Request SSL certificate via Let's Encrypt">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              disabled={certLoading === row.domain}
                              onClick={() => void handleCert(row)}
                              startIcon={
                                certLoading === row.domain
                                  ? <CircularProgress size={14} />
                                  : <HttpsIcon />
                              }
                            >
                              Get SSL
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {isPrimary ? (
                        <Tooltip title="Primary domain — cannot be removed">
                          <LockIcon fontSize="small" color="disabled" sx={{ mt: 0.5 }} />
                        </Tooltip>
                      ) : canWrite ? (
                        <Tooltip title="Remove subdomain">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={deleteLoading === row.domain}
                              onClick={() => void handleDelete(row)}
                            >
                              {deleteLoading === row.domain
                                ? <CircularProgress size={16} />
                                : <DeleteIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <AddSubdomainDialog
        open={addOpen}
        rootDomain={cfg.root_domain}
        onClose={onCloseAdd}
        onAdded={refresh}
      />
    </>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function Webserver() {
  const canRead  = usePermission('webserver.read');
  const canWrite = usePermission('webserver.write');
  const [tab, setTab]         = useState(0);
  const [addOpen, setAddOpen] = useState(false);

  if (!canRead) {
    return <Alert severity="error">You do not have permission to view webserver info.</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5">Webserver</Typography>
        {tab === 1 && canWrite && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            Add Subdomain
          </Button>
        )}
      </Stack>

      <InfoCard />

      <Tabs
        value={tab}
        onChange={(_, v: number) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Server Blocks" />
        <Tab label="Subdomains" />
      </Tabs>

      {tab === 0 && <ServerBlocksTab canRead={canRead} />}
      {tab === 1 && (
        <SubdomainsTab
          canRead={canRead}
          canWrite={canWrite}
          addOpen={addOpen}
          onCloseAdd={() => setAddOpen(false)}
        />
      )}
    </Box>
  );
}
